/** UTC display format used across md-ai (Model Config, satellite epoch, etc.) */
export const UTC_DISPLAY_FORMAT = 'YYYY-MM-DD HH:MM:SS';

export const DISPLAY_PATTERN = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;

/** True when string is exactly 19 chars matching YYYY-MM-DD HH:MM:SS */
export function isCompleteUtcDisplay(raw: string): boolean {
  return DISPLAY_PATTERN.test(raw.trim());
}

/** Validate real UTC calendar date (e.g. rejects 2026-02-30). */
export function isValidUtcCalendarDate(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1) return false;
  const d = new Date(Date.UTC(year, month - 1, day));
  return d.getUTCFullYear() === year && d.getUTCMonth() === month - 1 && d.getUTCDate() === day;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** Strip to digits only (max 14 for full datetime). */
export function digitsOnly(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 14);
}

/**
 * Build display string from up to 14 digits: YYYYMMDDHHmmss → YYYY-MM-DD HH:MM:SS
 */
export function formatUtcInputWhileTyping(raw: string): string {
  const d = digitsOnly(raw);
  if (!d.length) return '';

  const y = d.slice(0, 4);
  const mo = d.slice(4, 6);
  const day = d.slice(6, 8);
  const h = d.slice(8, 10);
  const mi = d.slice(10, 12);
  const s = d.slice(12, 14);

  let out = y;
  if (mo.length) out += `-${mo}`;
  if (day.length) out += `-${day}`;
  if (h.length) out += ` ${h}`;
  if (mi.length) out += `:${mi}`;
  if (s.length) out += `:${s}`;
  return out;
}

/** Validation for partial/incomplete manual input while typing. */
export function validateUtcDisplayPartial(raw: string): string {
  const t = raw.trim();
  if (!t) return '';
  if (isCompleteUtcDisplay(t)) return validateUtcDisplay(t);
  if (t.length >= 19) return `Use format ${UTC_DISPLAY_FORMAT}`;
  // Allow partial segments but flag obvious bad prefixes
  const digitLen = digitsOnly(t).length;
  if (digitLen > 0 && digitLen < 14) return `Use format ${UTC_DISPLAY_FORMAT}`;
  return `Use format ${UTC_DISPLAY_FORMAT}`;
}

export function validateUtcDisplay(raw: string): string {
  if (!raw.trim()) return 'Required';
  if (!DISPLAY_PATTERN.test(raw.trim())) return `Use format ${UTC_DISPLAY_FORMAT}`;
  const [datePart, timePart] = raw.trim().split(' ');
  const [y, mo, day] = datePart.split('-').map(Number);
  const [h, mi, s] = timePart.split(':').map(Number);
  if (!Number.isFinite(y) || y < 1970 || y > 2100) return 'Invalid year';
  if (mo < 1 || mo > 12) return 'Invalid month';
  if (day < 1 || day > 31) return 'Invalid day';
  if (!isValidUtcCalendarDate(y, mo, day)) return 'Invalid date';
  if (h > 23) return 'Invalid hour';
  if (mi > 59) return 'Invalid minute';
  if (s > 59) return 'Invalid second';
  const epoch = displayToEpoch(raw);
  if (epoch === null) return 'Invalid date';
  return '';
}

export function displayToEpoch(display: string, timezone: 'UTC' | 'IST' = 'UTC'): number | null {
  const raw = display.trim();
  if (!DISPLAY_PATTERN.test(raw)) return null;
  const suffix = timezone === 'IST' ? '+05:30' : 'Z';
  const iso = raw.replace(' ', 'T') + suffix;
  const ms = new Date(iso).getTime();
  if (!Number.isFinite(ms)) return null;
  return Math.floor(ms / 1000);
}

export function epochToDisplay(epoch: number): string {
  const d = new Date(epoch * 1000);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
}

export function getUtcNowDisplay(): string {
  return epochToDisplay(Math.floor(Date.now() / 1000));
}

export function addHoursToDisplay(display: string, hours: number): string {
  const epoch = displayToEpoch(display);
  if (epoch === null) return display;
  return epochToDisplay(epoch + hours * 3600);
}

export interface UtcParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
  second: number;
}

export function displayToParts(display: string): UtcParts | null {
  const epoch = displayToEpoch(display);
  if (epoch === null) return null;
  const d = new Date(epoch * 1000);
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    hour: d.getUTCHours(),
    minute: d.getUTCMinutes(),
    second: d.getUTCSeconds(),
  };
}

export function partsToDisplay(p: UtcParts): string {
  return `${p.year}-${pad(p.month)}-${pad(p.day)} ${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}`;
}

export function getUtcNowParts(): UtcParts {
  const d = new Date();
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    hour: d.getUTCHours(),
    minute: d.getUTCMinutes(),
    second: d.getUTCSeconds(),
  };
}

/** Days in month (UTC calendar, month 1-12). */
export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
